import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  EmojiEvents as ContestIcon,
  ExitToApp as LogoutIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import ContestTasksPage from '@components/performance/ContestTasksPage';
import { ContestHubHeader } from '@components/contest/ContestHubUI';
import { CONTEST_CATEGORIES, CONTEST_VIEWS, type ContestView } from '@utils/contest';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { loadPlayerProfileForUser, buildFallbackPlayerProfile } from '@utils/coachPlayerProfile';
import { toSportLabel } from '@utils/sports';
import { getSportIcon, getSportTheme } from '@utils/sportPresentation';

const DRAWER_WIDTH = 300;
const DRAWER_WIDTH_COLLAPSED = 64;

interface ContestSportPageProps {
  portalMode?: PortalMode;
}

const ContestSportPage: React.FC<ContestSportPageProps> = ({ portalMode = 'player' }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { sport: sportSlug, category: categorySlug = 'all' } = useParams<{ sport: string; category: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const paths = getPortalPaths(portalMode);

  const sport = toSportLabel(sportSlug);
  const category = (categorySlug || 'all').toLowerCase();
  const view = (searchParams.get('view') as ContestView) || 'browse';
  const highlightTaskId = searchParams.get('taskId') || undefined;
  const theme = getSportTheme(sport);

  const [player, setPlayer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);

  useEffect(() => {
    const loadPlayer = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        const profile = await loadPlayerProfileForUser(user.id, token, true, user);
        setPlayer(profile || buildFallbackPlayerProfile(user));
      } catch (err: any) {
        setError(err?.message || 'Failed to load player profile');
        setPlayer(buildFallbackPlayerProfile(user));
      } finally {
        setLoading(false);
      }
    };
    if (user) void loadPlayer();
  }, [user]);

  const handleCategoryChange = (nextCategory: string) => {
    navigate(paths.contestSport(sport, nextCategory, view));
  };

  const handleViewChange = (_: React.SyntheticEvent, nextView: ContestView) => {
    const next = new URLSearchParams(searchParams);
    if (nextView === 'browse') {
      next.delete('view');
    } else {
      next.set('view', nextView);
    }
    setSearchParams(next);
  };

  const drawer = (
    <Box sx={{ bgcolor: '#2c3e50', height: '100%', display: 'flex', flexDirection: 'column', color: '#ecf0f1' }}>
      <Toolbar sx={{ bgcolor: '#1a252f', minHeight: '56px !important', justifyContent: 'space-between' }}>
        {!sidebarCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EncycdataLogo height={24} lightBackground />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#ff9800' }}>
              Contests
            </Typography>
          </Box>
        )}
        <IconButton onClick={() => setSidebarCollapsed(!sidebarCollapsed)} sx={{ color: '#ff9800' }}>
          {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>

      <List sx={{ flexGrow: 1, pt: 1 }}>
        <List component="div" disablePadding>
          <ListItemButton
            onClick={() => navigate(paths.contestOverview)}
            sx={{ color: '#ff9800', '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.1)' } }}
          >
            <ListItemIcon sx={{ color: '#ff9800', minWidth: sidebarCollapsed ? 0 : 40 }}>
              <ArrowBackIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText primary="Back to Contest Hub" primaryTypographyProps={{ fontWeight: 700, color: '#ff9800' }} />
            )}
          </ListItemButton>
        </List>

        {!sidebarCollapsed && (
          <Box sx={{ px: 2, py: 1.5, mx: 1.5, mb: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: theme.light,
                  color: theme.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {getSportIcon(sport, 24)}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#fff' }}>
                  {sport}
                </Typography>
                <Typography variant="caption" sx={{ color: '#bdc3c7' }}>
                  Contest formats
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <List component="div" disablePadding>
          <ListItemButton onClick={() => setCategoriesExpanded(!categoriesExpanded)} sx={{ color: '#ecf0f1' }}>
            <ListItemIcon sx={{ color: '#ff9800', minWidth: sidebarCollapsed ? 0 : 40 }}>
              <ContestIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <>
                <ListItemText primary="Formats" primaryTypographyProps={{ fontWeight: 800 }} />
                {categoriesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </>
            )}
          </ListItemButton>
        </List>

        <Collapse in={categoriesExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {CONTEST_CATEGORIES.map((item) => {
              const selected = category === item.id;
              return (
                <ListItemButton
                  key={item.id}
                  selected={selected}
                  onClick={() => handleCategoryChange(item.id)}
                  sx={{
                    pl: sidebarCollapsed ? 2 : 4,
                    color: selected ? '#fff' : '#bdc3c7',
                    bgcolor: selected ? '#c2410c' : 'transparent',
                    '&:hover': { bgcolor: selected ? '#9a3412' : '#34495e' },
                    '&.Mui-selected': { bgcolor: '#c2410c', '&:hover': { bgcolor: '#9a3412' } },
                  }}
                >
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selected ? 800 : 600 }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      </List>

      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <List component="div" disablePadding>
          <ListItemButton onClick={() => logout()} sx={{ color: '#ff9800', '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.1)' } }}>
            <ListItemIcon sx={{ color: '#ff9800', minWidth: sidebarCollapsed ? 0 : 56 }}>
              <LogoutIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600, color: '#ff9800' }} />
            )}
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );

  const renderMain = () => {
    if (loading || !player?.id) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    const handleContestViewChange = (nextView: ContestView, taskId?: string) => {
      const next = new URLSearchParams(searchParams);
      if (nextView === 'browse') {
        next.delete('view');
      } else {
        next.set('view', nextView);
      }
      if (taskId) {
        next.set('taskId', taskId);
      } else {
        next.delete('taskId');
      }
      setSearchParams(next);
    };

    return (
      <>
        <ContestHubHeader sportName={sport} />

        {category !== 'all' && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={CONTEST_CATEGORIES.find((c) => c.id === category)?.label || category}
              size="small"
              sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 800, border: '1px solid #fdba74' }}
            />
          </Box>
        )}

        <Tabs
          value={view}
          onChange={handleViewChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {CONTEST_VIEWS.map((item) => (
            <Tab key={item.id} value={item.id} label={item.label} sx={{ textTransform: 'none', fontWeight: 700 }} />
          ))}
        </Tabs>

        <ContestTasksPage
          player={player}
          sport={sport}
          view={view}
          categoryFilter={category}
          highlightTaskId={highlightTaskId}
          hideHeader
          onViewChange={handleContestViewChange}
        />
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="nav"
        sx={{
          width: { sm: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: '#2c3e50' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: '#2c3e50',
              transition: 'width 0.3s',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)` },
        }}
      >
        {/* Mobile top bar */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton onClick={() => setMobileOpen(true)} size="small" sx={{ color: '#2c3e50' }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#2c3e50' }}>
            {sport} — Contests
          </Typography>
        </Box>
        <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', sm: 'calc(100vw - 360px)' }, px: { xs: 0, sm: 1 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {renderMain()}
        </Container>
      </Box>
    </Box>
  );
};

export default ContestSportPage;
